import React, { useEffect, useState, useCallback, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import { TINGKATAN_LIST } from "@/lib/academic";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Search, Plus, Pencil, Trash2, Loader2, BookText } from "lucide-react";
import { toast } from "sonner";

function emptyForm() {
  return { nama: "", tingkatan: "", ustadz_id: "", is_active: true, keterangan: "" };
}

export default function MataPelajaranPage() {
  const [items, setItems] = useState([]);
  const [ustadzList, setUstadzList] = useState([]);
  const [q, setQ] = useState("");
  const [tFilter, setTFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (tFilter !== "all") params.tingkatan = tFilter;
      const { data } = await api.get("/mata-pelajaran", { params });
      let rows = data.items || [];
      if (q) rows = rows.filter((r) => r.nama.toLowerCase().includes(q.toLowerCase()));
      setItems(rows);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [q, tFilter]);

  const loadUstadz = useCallback(async () => {
    try {
      const { data } = await api.get("/ustadz", { params: { limit: 200 } });
      setUstadzList(data.items || []);
    } catch (_e) { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadUstadz(); }, [loadUstadz]);

  const ustadzMap = useMemo(() => {
    const m = {};
    ustadzList.forEach((u) => { m[u.id] = u.nama; });
    return m;
  }, [ustadzList]);

  const openCreate = () => { setEditing(null); setForm(emptyForm()); setDialogOpen(true); };
  const openEdit = (it) => {
    setEditing(it);
    setForm({
      nama: it.nama || "", tingkatan: it.tingkatan || "",
      ustadz_id: it.ustadz_id || "", is_active: it.is_active !== false,
      keterangan: it.keterangan || "",
    });
    setDialogOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.tingkatan) { toast.error("Tingkatan wajib dipilih"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.ustadz_id) payload.ustadz_id = null;
      if (editing) {
        await api.patch(`/mata-pelajaran/${editing.id}`, payload);
        toast.success("Mata pelajaran diperbarui");
      } else {
        await api.post("/mata-pelajaran", payload);
        toast.success("Mata pelajaran ditambahkan");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/mata-pelajaran/${deleteTarget.id}`);
      toast.success("Mata pelajaran dihapus");
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="mapel-page">
      <div className="flex flex-col gap-1">
        <div className="text-xs font-semibold text-emerald-700 uppercase tracking-widest">Akademik</div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 tracking-tight">Mata Pelajaran</h1>
        <p className="text-slate-500 text-sm">Daftar mata pelajaran per tingkatan dan ustadz pengampunya.</p>
      </div>

      <Card className="p-4 md:p-5 border border-slate-200 shadow-sm bg-white">
        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <div className="relative flex-1">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input placeholder="Cari nama mata pelajaran..." value={q} onChange={(e) => setQ(e.target.value)}
                   className="pl-9 h-10" data-testid="mapel-search-input" />
          </div>
          <Select value={tFilter} onValueChange={setTFilter}>
            <SelectTrigger className="w-full md:w-56 h-10" data-testid="mapel-tingkatan-filter">
              <SelectValue placeholder="Tingkatan" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Tingkatan</SelectItem>
              {TINGKATAN_LIST.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={openCreate} className="bg-emerald-600 hover:bg-emerald-700 text-white h-10 gap-2" data-testid="add-mapel-button">
            <Plus className="h-4 w-4" /> Tambah Mata Pelajaran
          </Button>
        </div>
      </Card>

      <Card className="border border-slate-200 shadow-sm bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Nama</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Tingkatan</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Ustadz Pengampu</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Status</TableHead>
                <TableHead className="text-right text-xs uppercase tracking-wider text-slate-500 font-semibold">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={5} className="text-center py-12 text-slate-400">
                  <Loader2 className="h-5 w-5 mx-auto animate-spin" />
                </TableCell></TableRow>
              ) : items.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center py-12 text-slate-400">
                  Belum ada mata pelajaran.
                </TableCell></TableRow>
              ) : items.map((it) => (
                <TableRow key={it.id} data-testid={`mapel-row-${it.id}`} className="hover:bg-emerald-50/40">
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-lg bg-violet-50 text-violet-700 flex items-center justify-center">
                        <BookText className="h-4 w-4" />
                      </div>
                      <div>
                        <div className="font-semibold text-slate-900">{it.nama}</div>
                        {it.keterangan && <div className="text-xs text-slate-500">{it.keterangan}</div>}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-slate-700">{it.tingkatan}</TableCell>
                  <TableCell className="text-sm text-slate-700">{ustadzMap[it.ustadz_id] || "-"}</TableCell>
                  <TableCell>
                    <span className={`px-2.5 py-0.5 inline-flex text-xs font-semibold rounded-full ${
                      it.is_active ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"
                    }`}>
                      {it.is_active ? "Aktif" : "Nonaktif"}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(it)} data-testid={`edit-mapel-${it.id}`}>
                      <Pencil className="h-4 w-4 text-slate-600" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(it)} data-testid={`delete-mapel-${it.id}`}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Mata Pelajaran" : "Tambah Mata Pelajaran"}</DialogTitle>
            <DialogDescription>Hubungkan ke tingkatan kelas dan ustadz pengampu.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div>
              <Label>Nama Mata Pelajaran *</Label>
              <Input required value={form.nama} onChange={(e) => setForm(f => ({ ...f, nama: e.target.value }))}
                     data-testid="mapel-form-nama" placeholder="Mis. Fiqih" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Tingkatan *</Label>
                <Select value={form.tingkatan} onValueChange={(v) => setForm(f => ({ ...f, tingkatan: v }))}>
                  <SelectTrigger data-testid="mapel-form-tingkatan"><SelectValue placeholder="Pilih tingkatan" /></SelectTrigger>
                  <SelectContent>
                    {TINGKATAN_LIST.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Ustadz Pengampu</Label>
                <Select value={form.ustadz_id || "none"} onValueChange={(v) => setForm(f => ({ ...f, ustadz_id: v === "none" ? "" : v }))}>
                  <SelectTrigger data-testid="mapel-form-ustadz"><SelectValue placeholder="Pilih ustadz" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Belum ditentukan</SelectItem>
                    {ustadzList.map((u) => <SelectItem key={u.id} value={u.id}>{u.nama}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Keterangan</Label>
              <Textarea rows={2} value={form.keterangan} onChange={(e) => setForm(f => ({ ...f, keterangan: e.target.value }))}
                        data-testid="mapel-form-keterangan" />
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm(f => ({ ...f, is_active: v }))}
                      data-testid="mapel-form-active" />
              <Label>Aktif</Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Batal</Button>
              <Button type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="mapel-form-submit">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Simpan"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus Mata Pelajaran?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-semibold">{deleteTarget?.nama}</span> akan dihapus.
              Jika sudah memiliki nilai, sistem menolak — silakan nonaktifkan saja.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} data-testid="mapel-delete-confirm" className="bg-red-600 hover:bg-red-700">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
